import React from 'react';
import { Alert, Badge, Card, Col, Row, Table } from 'react-bootstrap';
import ViewPdfButton from './ViewPdfButton';

const SERVICE_LABELS = {
  TIKANE: 'Ti Kanè',
  TIKANE_SENP: 'Ti Kanè Senp',
  ZELLE: 'Zelle',
  MONCASH: 'MonCash',
  NATCASH: 'NatCash',
  MICRO_CREDIT: 'Micro Crédit',
  EXCHANGE: 'Exchange System',
};

const SERVICE_KEYS = Object.keys(SERVICE_LABELS);

const fmt = (value) =>
  Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR');
};

function amount(report, section, currency, serviceKey) {
  return fmt(report?.breakdown?.[section]?.[currency]?.[serviceKey]);
}

export default function CashCombinedReport({ report }) {
  if (!report) return null;

  if (!report.sessionCount) {
    return (
      <Alert variant="info" className="mb-0">
        Aucune session clôturée ne correspond à cette période.
      </Alert>
    );
  }

  const totals = report.totals || {};
  const firstSession = report.firstSession || {};
  const lastSession = report.lastSession || {};

  const getDocument = () => ({
    title: `Rapport combiné de caisse - ${report.user?.username || 'Utilisateur'}`,
    sections: [
      {
        title: 'Période et portée',
        rows: [
          { label: 'Utilisateur', value: report.user?.username || '—' },
          { label: 'Nombre de sessions incluses', value: String(report.sessionCount || 0) },
          { label: 'Début de période', value: formatDate(report.period?.dateFrom) },
          { label: 'Fin de période', value: formatDate(report.period?.dateTo) },
          { label: 'Première ouverture', value: formatDate(firstSession.openedAt) },
          { label: 'Dernière fermeture', value: formatDate(lastSession.closedAt) },
        ],
      },
      {
        title: 'Première ouverture et dernière clôture',
        rows: [
          { label: 'Premier Open USD', value: `${fmt(firstSession.openUsd)} USD` },
          { label: 'Premier Open HTG', value: `${fmt(firstSession.openHtg)} HTG` },
          { label: 'Dernier solde système USD', value: `${fmt(lastSession.systemBalanceUsd)} USD` },
          { label: 'Dernier solde système HTG', value: `${fmt(lastSession.systemBalanceHtg)} HTG` },
          { label: 'Dernier solde réel USD', value: `${fmt(lastSession.realBalanceUsd)} USD` },
          { label: 'Dernier solde réel HTG', value: `${fmt(lastSession.realBalanceHtg)} HTG` },
        ],
      },
      {
        title: 'Totaux consolidés',
        rows: [
          { label: 'Apports USD', value: `${fmt(totals.apportUsd)} USD` },
          { label: 'Apports HTG', value: `${fmt(totals.apportHtg)} HTG` },
          { label: 'Retraits USD', value: `${fmt(totals.retraitUsd)} USD` },
          { label: 'Retraits HTG', value: `${fmt(totals.retraitHtg)} HTG` },
          { label: 'Total encaissé USD', value: `${fmt(totals.totalEncaisseUsd)} USD` },
          { label: 'Total encaissé HTG', value: `${fmt(totals.totalEncaisseHtg)} HTG` },
          { label: 'Total décaissé USD', value: `${fmt(totals.totalDecaisseUsd)} USD` },
          { label: 'Total décaissé HTG', value: `${fmt(totals.totalDecaisseHtg)} HTG` },
          { label: 'Frais encaissés USD (information)', value: `${fmt(totals.totalFeesEncaisseUsd)} USD` },
          { label: 'Frais encaissés HTG (information)', value: `${fmt(totals.totalFeesEncaisseHtg)} HTG` },
          { label: 'Écarts cumulés USD', value: `${fmt(totals.differenceUsd)} USD` },
          { label: 'Écarts cumulés HTG', value: `${fmt(totals.differenceHtg)} HTG` },
        ],
      },
      {
        title: 'Détail consolidé par service',
        rows: SERVICE_KEYS.flatMap((serviceKey) => [
          {
            label: `${SERVICE_LABELS[serviceKey]} - encaissé`,
            value: `${amount(report, 'encaisse', 'USD', serviceKey)} USD / ${amount(
              report,
              'encaisse',
              'HTG',
              serviceKey
            )} HTG`,
          },
          {
            label: `${SERVICE_LABELS[serviceKey]} - décaissé`,
            value: `${amount(report, 'decaisse', 'USD', serviceKey)} USD / ${amount(
              report,
              'decaisse',
              'HTG',
              serviceKey
            )} HTG`,
          },
          {
            label: `${SERVICE_LABELS[serviceKey]} - frais`,
            value: `${amount(report, 'fees', 'USD', serviceKey)} USD / ${amount(
              report,
              'fees',
              'HTG',
              serviceKey
            )} HTG`,
          },
        ]),
      },
    ],
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-1">Rapport combiné</h5>
          <div className="text-muted small">
            {formatDate(report.period?.dateFrom)} au {formatDate(report.period?.dateTo)}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Badge bg="primary">{report.sessionCount} session(s)</Badge>
          <ViewPdfButton
            getDocument={getDocument}
            label="Télécharger le rapport combiné"
            variant="outline-dark"
          />
        </div>
      </div>

      <Alert variant="secondary" className="small">
        Les apports, retraits, encaissements, décaissements, frais et écarts sont additionnés sur
        toutes les sessions de la période. Les ouvertures et fermetures ne sont pas additionnées :
        le rapport présente la première ouverture et la dernière clôture afin d’éviter le double
        comptage du fonds de caisse.
      </Alert>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="fw-semibold">Portée du rapport</Card.Header>
            <Card.Body>
              <Table bordered size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <th>Utilisateur</th>
                    <td>{report.user?.username || '—'}</td>
                  </tr>
                  <tr>
                    <th>Sessions incluses</th>
                    <td>{report.sessionCount}</td>
                  </tr>
                  <tr>
                    <th>Première ouverture</th>
                    <td>{formatDate(firstSession.openedAt)}</td>
                  </tr>
                  <tr>
                    <th>Dernière fermeture</th>
                    <td>{formatDate(lastSession.closedAt)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="fw-semibold">Ouverture et clôture de référence</Card.Header>
            <Card.Body>
              <Table bordered size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <th>Premier Open USD</th>
                    <td>{fmt(firstSession.openUsd)}</td>
                    <th>Premier Open HTG</th>
                    <td>{fmt(firstSession.openHtg)}</td>
                  </tr>
                  <tr>
                    <th>Dernier solde système USD</th>
                    <td>{fmt(lastSession.systemBalanceUsd)}</td>
                    <th>Dernier solde système HTG</th>
                    <td>{fmt(lastSession.systemBalanceHtg)}</td>
                  </tr>
                  <tr>
                    <th>Dernier solde réel USD</th>
                    <td>{fmt(lastSession.realBalanceUsd)}</td>
                    <th>Dernier solde réel HTG</th>
                    <td>{fmt(lastSession.realBalanceHtg)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Totaux consolidés de la période</Card.Header>
        <Card.Body>
          <Table bordered responsive size="sm" className="mb-0">
            <tbody>
              <tr>
                <th>Apports USD</th>
                <td>{fmt(totals.apportUsd)}</td>
                <th>Apports HTG</th>
                <td>{fmt(totals.apportHtg)}</td>
              </tr>
              <tr>
                <th>Retraits USD</th>
                <td>{fmt(totals.retraitUsd)}</td>
                <th>Retraits HTG</th>
                <td>{fmt(totals.retraitHtg)}</td>
              </tr>
              <tr>
                <th>Total encaissé USD</th>
                <td>{fmt(totals.totalEncaisseUsd)}</td>
                <th>Total encaissé HTG</th>
                <td>{fmt(totals.totalEncaisseHtg)}</td>
              </tr>
              <tr>
                <th>Total décaissé USD</th>
                <td>{fmt(totals.totalDecaisseUsd)}</td>
                <th>Total décaissé HTG</th>
                <td>{fmt(totals.totalDecaisseHtg)}</td>
              </tr>
              <tr>
                <th>Frais USD (information)</th>
                <td>{fmt(totals.totalFeesEncaisseUsd)}</td>
                <th>Frais HTG (information)</th>
                <td>{fmt(totals.totalFeesEncaisseHtg)}</td>
              </tr>
              <tr>
                <th>Écarts cumulés USD</th>
                <td>{fmt(totals.differenceUsd)}</td>
                <th>Écarts cumulés HTG</th>
                <td>{fmt(totals.differenceHtg)}</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="fw-semibold">Répartition consolidée par service</Card.Header>
        <Card.Body>
          <Table bordered hover responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th rowSpan={2} className="align-middle">Service</th>
                <th colSpan={2} className="text-center">Encaissé</th>
                <th colSpan={2} className="text-center">Décaissé</th>
                <th colSpan={2} className="text-center">Frais</th>
              </tr>
              <tr>
                <th>USD</th>
                <th>HTG</th>
                <th>USD</th>
                <th>HTG</th>
                <th>USD</th>
                <th>HTG</th>
              </tr>
            </thead>
            <tbody>
              {SERVICE_KEYS.map((serviceKey) => (
                <tr key={serviceKey}>
                  <th>{SERVICE_LABELS[serviceKey]}</th>
                  <td>{amount(report, 'encaisse', 'USD', serviceKey)}</td>
                  <td>{amount(report, 'encaisse', 'HTG', serviceKey)}</td>
                  <td>{amount(report, 'decaisse', 'USD', serviceKey)}</td>
                  <td>{amount(report, 'decaisse', 'HTG', serviceKey)}</td>
                  <td>{amount(report, 'fees', 'USD', serviceKey)}</td>
                  <td>{amount(report, 'fees', 'HTG', serviceKey)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
}
